import { SaveDialogContent, SaveButton } from "@/components/SaveForm";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { partPayload } from "@/lib/partPayload";
import api, { apiErr } from "@/lib/api";
import { ProtectedImage } from "@/components/ProtectedImage";
import { Dialog, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Loader2 } from "lucide-react";

const EMPTY = {
  descricao: "", descricao_tecnica: "", codigo_interno: "", codigo_omie: "", categoria_id: "",
  fabricante: "", fornecedor_id: "", codigo_fornecedor: "", unidade: "UN", foto_principal: "",
  quantidade_atual: 0, estoque_minimo: 0, estoque_seguranca: 0, estoque_ideal: 0, custo_medio: 0,
  localizacao_id: "", criticidade: "MEDIA", lead_time_dias: 15, origem: "NACIONAL", pais_origem: "Brasil",
  observacoes_tecnicas: "", status: "ATIVO", aplicacoes: [],
};

const Field = ({ label, children }) => (
  <div><Label className="text-slate-300 text-xs">{label}</Label><div className="mt-1">{children}</div></div>
);
const inp = "bg-slate-950 border-slate-700 text-slate-100";

export function PartForm({ open, onOpenChange, part, onSaved }) {
  const { canFinance } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [cats, setCats] = useState([]);
  const [forns, setForns] = useState([]);
  const [locs, setLocs] = useState([]);
  const [machines, setMachines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const savingRef = useRef(false);
  const uploadsRef = useRef(0);
  const beginUpload = () => { uploadsRef.current += 1; setUploading(true); };
  const endUpload = () => { uploadsRef.current -= 1; setUploading(uploadsRef.current > 0); };

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    Promise.all([api.get("/categories"), api.get("/suppliers"), api.get("/locations"), api.get("/machines"),
      part ? api.get(`/parts/${part.id}`) : Promise.resolve({ data: null })])
      .then(([c, f, l, m, detail]) => {
        if (!active) return;
        setCats(c.data); setForns(f.data); setLocs(l.data); setMachines(m.data);
        const p = detail.data;
        setForm(p ? { ...EMPTY, ...p, aplicacoes: (p.maquinas || []).map((mq) => ({
          machine_id: mq.id, posicao_instalacao: mq.posicao_instalacao || "",
          quantidade_por_maquina: mq.quantidade_por_maquina || 1, foto_instalacao: mq.foto_instalacao || "",
        })) } : EMPTY);
        setLoading(false);
      })
      .catch(e => { if (active) toast.error(apiErr(e)); });
    return () => { active = false; };
  }, [open, part]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || savingRef.current) return;
    beginUpload();
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("foto_principal", data.path);
      toast.success("Foto enviada.");
    } catch (err) { toast.error(apiErr(err)); } finally { endUpload(); }
  };

  const addApp = () => set("aplicacoes", [...form.aplicacoes, { machine_id: "", posicao_instalacao: "", quantidade_por_maquina: 1, foto_instalacao: "" }]);
  const setApp = (i, k, v) => { const a = [...form.aplicacoes]; a[i][k] = v; set("aplicacoes", a); };
  const rmApp = (i) => set("aplicacoes", form.aplicacoes.filter((_, x) => x !== i));

  const uploadApp = async (i, e) => {
    const file = e.target.files?.[0];
    if (!file || savingRef.current) return;
    beginUpload();
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setApp(i, "foto_instalacao", data.path);
      toast.success("Foto de instalação enviada.");
    } catch (err) { toast.error(apiErr(err)); } finally { endUpload(); }
  };

  const save = async () => {
    if (loading || savingRef.current || uploadsRef.current > 0) return;
    if (!form.descricao.trim()) { toast.error("Descrição é obrigatória."); return; }
    savingRef.current = true;
    setSaving(true);
    try {
      const payload = partPayload(form, canFinance);
      if (part) await api.put(`/parts/${part.id}`, payload);
      else await api.post("/parts", payload);
      toast.success(part ? "Peça atualizada." : "Peça cadastrada.");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(apiErr(e)); } finally { savingRef.current = false; setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <SaveDialogContent saveBlocked={loading || saving || uploading} className="bg-slate-900 border-slate-700 text-slate-100 max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">{part ? "Editar peça" : "Nova peça"}</DialogTitle></DialogHeader>

        <div className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Descrição *"><Input data-testid="input-part-descricao" value={form.descricao} onChange={(e) => set("descricao", e.target.value)} className={inp} /></Field>
            <Field label="Código interno"><Input value={form.codigo_interno} onChange={(e) => set("codigo_interno", e.target.value)} className={inp} placeholder="Gerado automaticamente" /></Field>
          </div>
          <Field label="Descrição técnica"><Textarea value={form.descricao_tecnica} onChange={(e) => set("descricao_tecnica", e.target.value)} className={inp} rows={2} /></Field>

          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Categoria">
              <Select value={form.categoria_id} onValueChange={(v) => set("categoria_id", v)}>
                <SelectTrigger className={inp}><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Fornecedor">
              <Select value={form.fornecedor_id} onValueChange={(v) => set("fornecedor_id", v)}>
                <SelectTrigger className={inp}><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">{forns.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Localização física">
              <Select value={form.localizacao_id} onValueChange={(v) => set("localizacao_id", v)}>
                <SelectTrigger className={inp}><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">{locs.map((c) => <SelectItem key={c.id} value={c.id}>{[c.almoxarifado, c.estante, c.prateleira, c.caixa].filter(Boolean).join(" · ")}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid sm:grid-cols-4 gap-3">
            <Field label="Fabricante"><Input value={form.fabricante} onChange={(e) => set("fabricante", e.target.value)} className={inp} /></Field>
            <Field label="Cód. fornecedor"><Input value={form.codigo_fornecedor} onChange={(e) => set("codigo_fornecedor", e.target.value)} className={inp} /></Field>
            <Field label="Cód. Omie (futuro)"><Input value={form.codigo_omie} onChange={(e) => set("codigo_omie", e.target.value)} className={inp} /></Field>
            <Field label="Unidade"><Input value={form.unidade} onChange={(e) => set("unidade", e.target.value)} className={inp} /></Field>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Field label="Saldo (via movimentação)"><Input type="number" value={part ? form.quantidade_atual : 0} disabled className={inp} /></Field>
            <Field label="Estoque mín."><Input type="number" value={form.estoque_minimo} onChange={(e) => set("estoque_minimo", e.target.value)} className={inp} /></Field>
            <Field label="Est. segurança"><Input type="number" value={form.estoque_seguranca} onChange={(e) => set("estoque_seguranca", e.target.value)} className={inp} /></Field>
            <Field label="Estoque ideal"><Input type="number" value={form.estoque_ideal} onChange={(e) => set("estoque_ideal", e.target.value)} className={inp} /></Field>
            {canFinance && <Field label="Custo médio (R$)"><Input type="number" min="0" step="0.01" value={form.custo_medio} onChange={(e) => set("custo_medio", e.target.value)} className={inp} /></Field>}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Criticidade">
              <Select value={form.criticidade} onValueChange={(v) => set("criticidade", v)}>
                <SelectTrigger className={inp}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100"><SelectItem value="ALTA">Alta</SelectItem><SelectItem value="MEDIA">Média</SelectItem><SelectItem value="BAIXA">Baixa</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Lead time (dias)"><Input type="number" value={form.lead_time_dias} onChange={(e) => set("lead_time_dias", e.target.value)} className={inp} /></Field>
            <Field label="Origem">
              <Select value={form.origem} onValueChange={(v) => set("origem", v)}>
                <SelectTrigger className={inp}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100"><SelectItem value="NACIONAL">Nacional</SelectItem><SelectItem value="IMPORTADA">Importada</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="País de origem"><Input value={form.pais_origem} onChange={(e) => set("pais_origem", e.target.value)} className={inp} /></Field>
          </div>

          <Field label="Foto principal">
            <div className="flex items-center gap-3">
              {form.foto_principal && <ProtectedImage src={form.foto_principal} alt="" className="h-16 w-16 rounded object-cover border border-slate-700" />}
              <label className="cursor-pointer inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm hover:bg-slate-800">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {form.foto_principal ? "Substituir foto" : "Enviar foto"}
                <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={saving} data-testid="input-part-photo" />
              </label>
              {form.foto_principal && (
                <Button type="button" size="sm" variant="ghost" onClick={() => set("foto_principal", "")}
                  className="text-rose-400 hover:text-rose-300 hover:bg-rose-950/40" data-testid="btn-remove-part-photo">
                  <Trash2 className="h-4 w-4 mr-1" /> Remover foto
                </Button>
              )}
            </div>
          </Field>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-slate-300">Aplicação em máquinas</Label>
              <Button size="sm" variant="secondary" onClick={addApp} data-testid="btn-add-application"><Plus className="h-4 w-4 mr-1" /> Adicionar máquina</Button>
            </div>
            <div className="space-y-2">
              {form.aplicacoes.map((a, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-4">
                    <Select value={a.machine_id} onValueChange={(v) => setApp(i, "machine_id", v)}>
                      <SelectTrigger className={inp}><SelectValue placeholder="Máquina" /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">{machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3"><Input placeholder="Posição de instalação" value={a.posicao_instalacao} onChange={(e) => setApp(i, "posicao_instalacao", e.target.value)} className={inp} /></div>
                  <div className="col-span-2"><Input type="number" placeholder="Qtd" value={a.quantidade_por_maquina} onChange={(e) => setApp(i, "quantidade_por_maquina", Number(e.target.value))} className={inp} /></div>
                  <div className="col-span-2 flex items-center gap-1">
                    <label className="cursor-pointer flex flex-1 items-center justify-center gap-1 rounded-lg border border-slate-700 bg-slate-950 h-10 text-xs hover:bg-slate-800" title="Foto de onde a peça é instalada">
                      {a.foto_instalacao ? <ProtectedImage src={a.foto_instalacao} alt="" className="h-8 w-8 rounded object-cover" /> : <><Upload className="h-3.5 w-3.5" /> Instalação</>}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadApp(i, e)} disabled={saving} data-testid={`input-install-photo-${i}`} />
                    </label>
                    {a.foto_instalacao && (
                      <Button type="button" size="icon" variant="ghost" onClick={() => setApp(i, "foto_instalacao", "")}
                        title="Remover somente a foto de instalação" className="h-8 w-8 shrink-0 text-rose-400 hover:text-rose-300 hover:bg-rose-950/40"
                        data-testid={`btn-remove-install-photo-${i}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <div className="col-span-1"><Button size="icon" variant="ghost" onClick={() => rmApp(i)} className="text-rose-400" title="Remover máquina associada" data-testid={`btn-remove-application-${i}`}><Trash2 className="h-4 w-4" /></Button></div>
                </div>
              ))}
              {form.aplicacoes.length === 0 && <p className="text-xs text-slate-500">Nenhuma máquina associada.</p>}
            </div>
          </div>

          <Field label="Observações técnicas"><Textarea value={form.observacoes_tecnicas} onChange={(e) => set("observacoes_tecnicas", e.target.value)} className={inp} rows={2} /></Field>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <SaveButton data-testid="btn-save-part" onClick={save} disabled={loading || saving || uploading} className="bg-sky-700 hover:bg-sky-600">{saving ? "Salvando..." : "Salvar peça"}</SaveButton>
        </DialogFooter>
      </SaveDialogContent>
    </Dialog>
  );
}
